import { iconNavBusca, iconNavFechar, iconDoc2aVia } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsIcon, BsdsInput } from '@bsds-lib-component/react';
import React, { useId, useState } from 'react';

import { ParticipantTable, SystemMessage, useSystemContainer, Visibility, ZipCodeInput } from '../../../components';
import Button from '../../../components/button/Button';
import Pagination from '../../../components/pagination/Pagination';
import Radio from '../../../components/radio/Radio';
import { usePrevidenciaParticipant } from '../../../hooks';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import type { ParticipantSearchRequest, ParticipantTableItem, ParticipantUpdateRequestDTO } from '../../../types';
import { formatCpf } from '../../../utils/cpfUtils';
import { generateParticipantsXLSX } from '../../../utils/participantExcelUtils';

// Helper function to format date from yyyy-MM-dd to dd/MM/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

// Helper function to map biological sex
const mapBiologicalSex = (sex: string): string => {
  const sexMap: Record<string, string> = {
    MASCULINO: 'Masculino',
    FEMININO: 'Feminino',
    OUTRO: 'Outro',
  };
  return sexMap[sex] || sex;
};

// Helper function to map marital status
const mapMaritalStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    SOLTEIRO: 'Solteiro(a)',
    CASADO: 'Casado(a)',
    DIVORCIADO: 'Divorciado(a)',
    VIUVO: 'Viúvo(a)',
    UNIAO_ESTAVEL: 'União Estável',
  };
  return statusMap[status] || status;
};

// Helper function to map participant status
const mapStatus = (status: string): string => {
  const statusMap: Record<string, string> = {
    ATIVO: 'Ativo',
    INATIVO: 'Inativo',
    SUSPENSO: 'Suspenso',
    CANCELADO: 'Cancelado',
  };
  return statusMap[status] || status;
};

const SearchParticipantTab: React.FC = () => {
  const {
    participants,
    participantDetail,
    isLoading,
    clearError,
    searchParticipants,
    getParticipantDetail,
    updateParticipant,
    clearParticipantDetail,
    totalPages,
    totalElements,
    currentPage,
  } = usePrevidenciaParticipant();
  const { showMessage, message, type, show } = useSystemMessage();
  const { setLoading } = useSystemContainer();
  const mainContentId = useId();
  const searchFormId = useId();
  const resultsId = useId();
  const emptyStateId = useId();
  const messageId = useId();
  const detailsId = useId();

  // Search type state
  const [searchType, setSearchType] = useState<'contract' | 'name' | 'registration' | 'cpf'>('contract');

  // Search field states
  const [contractNumber, setContractNumber] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [cpf, setCpf] = useState('');

  // Store last search params to reuse when changing pages
  const [lastSearchParams, setLastSearchParams] = useState<ParticipantSearchRequest | null>(null);

  // State to control if there are search results
  const [hasSearchResults, setHasSearchResults] = useState<boolean>(false);
  // State to track if a search was performed
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  // State to control if showing details view
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState(false);
  // Flag to track if we just saved to prevent showing search message
  const [justSaved, setJustSaved] = useState<boolean>(false);

  // Form states for editing
  const [editForm, setEditForm] = useState<ParticipantUpdateRequestDTO | null>(null);
  // Store initial form values to compare changes
  const [initialFormValues, setInitialFormValues] = useState<ParticipantUpdateRequestDTO | null>(null);

  const handleSearch = async () => {
    // Check if the selected field is filled
    let hasValue = false;

    switch (searchType) {
      case 'contract':
        hasValue = !!contractNumber;
        break;
      case 'name':
        hasValue = !!participantName;
        break;
      case 'registration':
        hasValue = !!registrationNumber;
        break;
      case 'cpf':
        hasValue = !!cpf;
        break;
    }

    if (!hasValue) {
      clearError();
      showMessage('Por favor, preencha o campo selecionado para pesquisar', 'warning');
      return;
    }

    // Resets states before performing new search
    clearError();
    setHasSearchResults(false);
    setHasSearched(false);
    setShowDetails(false);
    setJustSaved(false);
    clearParticipantDetail();

    // Use default company ID (14 has previdencia participants in mocks)
    const companyId = '14';

    try {
      const searchParams: ParticipantSearchRequest = {
        companyId,
        ...(searchType === 'contract' && contractNumber && { contractNumber }),
        ...(searchType === 'name' && participantName && { participantName }),
        ...(searchType === 'registration' && registrationNumber && { registrationNumber }),
        ...(searchType === 'cpf' && cpf && { cpf: cpf.replace(/\D/g, '') }), // Remove formatting from CPF
        page: 0,
        size: 10,
      };

      setLastSearchParams(searchParams);
      await searchParticipants(searchParams);
      setHasSearched(true);
      // Success message will be shown via useEffect when participants are loaded
    } catch (err) {
      console.error('Erro ao buscar participantes:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao buscar participantes';
      showMessage(errorMsg, 'error');
      setHasSearchResults(false);
      setHasSearched(true);
    }
  };

  const handleClearFilters = () => {
    setContractNumber('');
    setParticipantName('');
    setRegistrationNumber('');
    setCpf('');
    setSearchType('contract');
    setHasSearchResults(false);
    setHasSearched(false);
    setShowDetails(false);
    setJustSaved(false);
    setLastSearchParams(null);
    clearError();
    clearParticipantDetail();
  };

  const handlePageChange = async (page: number) => {
    // Convert UI page (1-based) to backend page (0-based)
    const backendPage = page - 1;

    if (!lastSearchParams) {
      return;
    }

    try {
      const searchParams: ParticipantSearchRequest = {
        ...lastSearchParams,
        page: backendPage,
      };

      await searchParticipants(searchParams);
    } catch (err) {
      console.error('Erro ao mudar de página:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao mudar de página';
      showMessage(errorMsg, 'error');
    }
  };

  const handleSearchTypeChange = (newType: 'contract' | 'name' | 'registration' | 'cpf') => {
    // Clear all fields when changing search type
    setContractNumber('');
    setParticipantName('');
    setRegistrationNumber('');
    setCpf('');
    setSearchType(newType);
  };

  const handleInputChange = (setter: (value: string) => void) => (event: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = event.target as HTMLInputElement;
    setter(target?.value || '');
  };

  const handleCpfChange = (event: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = event.target as HTMLInputElement;
    const value = target?.value || '';
    setCpf(formatCpf(value));
  };

  const handleRowClick = async (participant: ParticipantTableItem) => {
    try {
      await getParticipantDetail(participant.participantEnrollmentId);
      setShowDetails(true);
      setIsEditMode(false);
    } catch (err) {
      console.error('Erro ao carregar detalhes do participante:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar detalhes do participante';
      showMessage(errorMsg, 'error');
    }
  };

  const handleBack = () => {
    setShowDetails(false);
    setIsEditMode(false);
    setEditForm(null);
    setInitialFormValues(null);
    setJustSaved(false);
    clearParticipantDetail();
  };

  const handleEdit = () => {
    if (!participantDetail) return;

    const initialForm: ParticipantUpdateRequestDTO = {
      participantEnrollmentId: participantDetail.participantEnrollmentId,
      participantId: participantDetail.participantId,
      fullName: participantDetail.participantName,
      registrationNumber: participantDetail.registrationNumber,
      cpf: participantDetail.cpf,
      birthDate: participantDetail.birthDate,
      biologicalSex: participantDetail.biologicalSex,
      maritalStatus: participantDetail.maritalStatus,
      cellPhone: participantDetail.cellPhone || '',
      email: participantDetail.email || '',
      zipCode: participantDetail.zipCode || '',
      district: participantDetail.district || '',
      state: participantDetail.state || '',
      city: participantDetail.city || '',
      street: participantDetail.street || '',
      number: participantDetail.number || '',
      complement: participantDetail.complement || '',
    };

    setEditForm(initialForm);
    setInitialFormValues(initialForm);
    setIsEditMode(true);
  };

  // Validate if all required fields are filled
  const validateRequiredFields = (form: ParticipantUpdateRequestDTO): boolean => {
    const requiredFields: (keyof ParticipantUpdateRequestDTO)[] = [
      'cellPhone',
      'email',
      'zipCode',
      'street',
      'number',
      'district',
      'city',
      'state',
    ];

    return requiredFields.every((field) => {
      const value = form[field];
      const stringValue = value !== null && value !== undefined ? String(value) : '';
      return stringValue.trim() !== '';
    });
  };

  // Check if form has changes compared to initial values
  const hasFormChanges = (): boolean => {
    if (!editForm || !initialFormValues) return false;

    const editableFields: (keyof ParticipantUpdateRequestDTO)[] = [
      'cellPhone',
      'email',
      'zipCode',
      'street',
      'number',
      'complement',
      'district',
      'city',
      'state',
    ];

    return editableFields.some((field) => {
      const currentValue = editForm[field] !== null && editForm[field] !== undefined ? String(editForm[field]) : '';
      const initialValue = initialFormValues[field] !== null && initialFormValues[field] !== undefined ? String(initialFormValues[field]) : '';
      return currentValue.trim() !== initialValue.trim();
    });
  };

  // Check if form is valid and has changes
  const isFormValid = (): boolean => {
    if (!editForm) return false;
    return validateRequiredFields(editForm) && hasFormChanges();
  };

  const handleSave = async () => {
    if (!editForm) return;

    // Validate required fields
    if (!validateRequiredFields(editForm)) {
      showMessage('Por favor, preencha todos os campos obrigatórios', 'warning');
      return;
    }

    // Check if there are changes
    if (!hasFormChanges()) {
      showMessage('Nenhuma alteração foi realizada', 'warning');
      return;
    }

    try {
      await updateParticipant(editForm);
      setJustSaved(true);
      showMessage('Participante atualizado com sucesso', 'success');
      setIsEditMode(false);
      setEditForm(null);
      setInitialFormValues(null);
      // Reset justSaved flag after a delay to allow the message to be seen (2 seconds)
      setTimeout(() => {
        setJustSaved(false);
      }, 2000);
    } catch (err) {
      console.error('Erro ao atualizar participante:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao atualizar participante';
      showMessage(errorMsg, 'error');
      setJustSaved(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditForm(null);
    setInitialFormValues(null);
    setJustSaved(false);
  };

  const handleEditFormChange = (field: keyof ParticipantUpdateRequestDTO, value: string) => {
    if (!editForm) return;
    setEditForm({ ...editForm, [field]: value });
  };

  const handleExport = (selectedParticipants: ParticipantTableItem[]) => {
    if (selectedParticipants.length === 0) {
      showMessage('Nenhum participante selecionado para exportar.', 'warning');
      return;
    }

    try {
      generateParticipantsXLSX(selectedParticipants, formatCpf);
      showMessage(`${selectedParticipants.length} participante(s) exportado(s) com sucesso.`, 'success');
    } catch (error) {
      console.error('Erro ao exportar participantes:', error);
      showMessage('Erro ao exportar os participantes. Tente novamente.', 'error');
    }
  };

  React.useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  // Sync hasSearchResults with participants after search and show success message
  React.useEffect(() => {
    // Don't show search message if we just saved or are in edit mode
    if (hasSearched && !isLoading && !justSaved && !isEditMode) {
      const hasResults = participants.length > 0;
      setHasSearchResults(hasResults);

      // Show success message only when we have results
      if (hasResults) {
        showMessage('Participantes encontrados com sucesso', 'success');
      }
    }
  }, [participants, hasSearched, isLoading, showMessage, justSaved, isEditMode]);

  // Map participants to table items
  const tableItems: ParticipantTableItem[] = participants.map((p) => ({
    ...p,
    id: p.participantEnrollmentId,
  }));

  // Convert backend page (0-based) to UI page (1-based) for Pagination component
  const uiCurrentPage = currentPage + 1;

  return (
    <div
      className="bsds-width-100 bsds-mt-12 bsds-p-8"
      role="main"
      aria-busy={isLoading}
      aria-label="Pesquisa de participantes"
      id={mainContentId}
    >
      {show && (
        <div
          id={messageId}
          className="bsds-mb-16"
          role={type === 'error' ? 'alert' : 'status'}
          aria-live={type === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <SystemMessage message={message} type={type} show={show} />
        </div>
      )}

      {/* Header */}
      <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
        <BsdsIcon icon={iconDoc2aVia} className="w-4 h-4" aria-hidden="true" />
        <h6 className="bsds-ml-8 font-semibold">Pesquisa de Participantes</h6>
      </div>

      {/* Search Form */}
      <div
        id={searchFormId}
        className="bsds-mb-24 mt-4 md:mt-8"
        role="region"
        aria-label="Formulário de busca de participantes"
      >
        <div className="bsds-mb-16">
          {/* Radio buttons for search type */}
          <div className="bsds-mb-16" role="radiogroup" aria-label="Tipo de busca">
            <div className="bsds-display-flex flex-col md:flex-row bsds-flex-wrap gap-2 md:gap-16">
              <div className="bsds-display-flex bsds-align-items-center bsds-gap-8">
                <Radio
                  checked={searchType === 'contract'}
                  onChange={(checked) => {
                    if (checked) {
                      handleSearchTypeChange('contract');
                    }
                  }}
                  aria-label="Buscar por Contrato"
                />
                <label className="bsds-text-regular cursor-pointer bsds-ml-8" onClick={() => handleSearchTypeChange('contract')}>
                  Contrato
                </label>
              </div>
              <div className="bsds-display-flex bsds-align-items-center bsds-gap-8">
                <Radio
                  checked={searchType === 'name'}
                  onChange={(checked) => {
                    if (checked) {
                      handleSearchTypeChange('name');
                    }
                  }}
                  aria-label="Buscar por Nome do Participante"
                />
                <label className="bsds-text-regular cursor-pointer bsds-ml-8" onClick={() => handleSearchTypeChange('name')}>
                  Nome do Participante
                </label>
              </div>
              <div className="bsds-display-flex bsds-align-items-center bsds-gap-8">
                <Radio
                  checked={searchType === 'registration'}
                  onChange={(checked) => {
                    if (checked) {
                      handleSearchTypeChange('registration');
                    }
                  }}
                  aria-label="Buscar por Número de Matrícula"
                />
                <label className="bsds-text-regular cursor-pointer bsds-ml-8" onClick={() => handleSearchTypeChange('registration')}>
                  Nº Matrícula
                </label>
              </div>
              <div className="bsds-display-flex bsds-align-items-center bsds-gap-8">
                <Radio
                  checked={searchType === 'cpf'}
                  onChange={(checked) => {
                    if (checked) {
                      handleSearchTypeChange('cpf');
                    }
                  }}
                  aria-label="Buscar por CPF do Participante"
                />
                <label className="bsds-text-regular cursor-pointer bsds-ml-8" onClick={() => handleSearchTypeChange('cpf')}>
                  CPF do Participante
                </label>
              </div>
            </div>
          </div>

          {/* Input field - shown based on selected radio */}
          <div className="bsds-mb-16">
            {searchType === 'contract' && (
              <BsdsInput
                value={contractNumber}
                onInput={handleInputChange(setContractNumber)}
                label="Número do Contrato"
                placeholder="Digite o número do contrato"
                type="text"
                color="primary"
                platform="web"
                className="bsds-width-100"
              />
            )}

            {searchType === 'name' && (
              <BsdsInput
                value={participantName}
                onInput={handleInputChange(setParticipantName)}
                label="Nome do Participante"
                placeholder="Digite o nome do participante"
                type="text"
                color="primary"
                platform="web"
                className="bsds-width-100"
              />
            )}

            {searchType === 'registration' && (
              <BsdsInput
                value={registrationNumber}
                onInput={handleInputChange(setRegistrationNumber)}
                label="Número de Matrícula"
                placeholder="Digite o número de matrícula"
                type="text"
                color="primary"
                platform="web"
                className="bsds-width-100"
              />
            )}

            {searchType === 'cpf' && (
              <BsdsInput
                value={cpf}
                onInput={handleCpfChange}
                label="CPF"
                placeholder="Digite o CPF"
                type="text"
                color="primary"
                platform="web"
                className="bsds-width-100"
              />
            )}
          </div>

          {/* Buttons */}
          <div
            className="bsds-display-flex bsds-justify-content-end gap-4"
            role="group"
            aria-label="Ações do formulário de busca"
          >
            <Button
              onClick={handleClearFilters}
              color="secondary"
              fill="outline"
              icon={iconNavFechar}
              iconPosition="end"
              aria-label="Limpar todos os filtros do formulário"
            >
              Limpar Filtros
            </Button>
            <Button
              onClick={handleSearch}
              color="primary"
              fill="solid"
              loading={isLoading}
              disabled={isLoading}
              icon={iconNavBusca}
              iconPosition="start"
              aria-label={isLoading ? 'Pesquisando participantes...' : 'Pesquisar participantes'}
            >
              Pesquisar
            </Button>
          </div>
        </div>
      </div>

      {/* Results content - visible only when there is data and not showing details */}
      <Visibility isActive={hasSearchResults && tableItems.length > 0 && !showDetails} animated animationDuration={300}>
        <div
          id={resultsId}
          className="bsds-mb-16 bsds-mt-34"
          role="region"
          aria-label="Resultados da busca de participantes"
        >
          <BsdsCard className="bsds-p-16">
            <ParticipantTable
              participants={tableItems}
              onRowClick={handleRowClick}
              emptyMessage="Nenhum participante encontrado"
              onExport={handleExport}
            />
          </BsdsCard>

          {/* Pagination Controls */}
          {!isLoading && totalElements > 0 && (
            <div className="bsds-mt-16 bsds-mb-16">
              <Pagination
                currentPage={uiCurrentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                totalItems={totalElements}
                itemLabel="participantes"
                alwaysShow
              />
            </div>
          )}
        </div>
      </Visibility>

      <Visibility isActive={hasSearched && !hasSearchResults && !showDetails} animated animationDuration={300}>
        <div
          id={emptyStateId}
          className="bsds-display-flex bsds-justify-content-center bsds-align-items-center bsds-mt-64 bsds-mb-64"
          role="status"
          aria-live="polite"
          aria-label="Resultado da busca"
        >
          <div className="bsds-text-center">
            <p className="bsds-text-regular bsds-text-lg text-gray-600">
              Dados não encontrados para a pesquisa realizada
            </p>
          </div>
        </div>
      </Visibility>

      {/* Detail Card - visible when showDetails is true */}
      <Visibility isActive={showDetails && !!participantDetail} animated animationDuration={300}>
        <div
          id={detailsId}
          className="bsds-mb-16 bsds-mt-34"
          role="region"
          aria-label={isEditMode ? 'Formulário de edição do participante' : 'Detalhes do participante'}
        >
          <BsdsCard className="bsds-p-16">
            {participantDetail && (
              <>
                {/* Header */}
                <div className="bsds-display-flex bsds-justify-content-space-between bsds-align-items-center bsds-mb-16 pb-4 border-b border-gray-100">
                  <div className="bsds-display-flex bsds-align-items-center gap-4">
                    <BsdsIcon icon={iconDoc2aVia} color="secondary" className="w-4 h-4" aria-hidden="true" />
                    <h6 className="text-lg font-semibold text-text-color">
                      {isEditMode ? 'Editar Participante' : 'Detalhes do Participante'}
                    </h6>
                  </div>

                </div>

                {/* Content */}
                <div className="overflow-y-auto custom-scrollbar">
                  {isEditMode && editForm ? (
                    <div className="flex flex-col gap-4">
                      <BsdsCard className="bsds-p-16">
                        <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                          <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
                          <h4 className="opacity-80">Dados Pessoais</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <BsdsInput
                            value={editForm.fullName}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('fullName', target?.value || '');
                            }}
                            label="Nome Completo"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                            disabled
                          />
                          <BsdsInput
                            value={formatCpf(editForm.cpf)}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('cpf', formatCpf(target?.value || ''));
                            }}
                            label="CPF"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                            disabled
                          />
                          <BsdsInput
                            value={editForm.registrationNumber}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('registrationNumber', target?.value || '');
                            }}
                            label="Número de Matrícula"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                            disabled
                          />
                          <BsdsInput
                            value={formatDate(editForm.birthDate)}
                            label="Data de Nascimento"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                            disabled
                          />
                          <BsdsInput
                            value={editForm.cellPhone}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('cellPhone', target?.value || '');
                            }}
                            label="Telefone Celular"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                          />
                          <BsdsInput
                            value={editForm.email}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('email', target?.value || '');
                            }}
                            label="E-mail"
                            type="email"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                          />
                        </div>
                      </BsdsCard>

                      <BsdsCard className="bsds-p-16">
                        <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                          <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
                          <h4 className="opacity-80">Endereço</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <ZipCodeInput
                            value={editForm.zipCode}
                            onChange={(value) => handleEditFormChange('zipCode', value)}
                            label="CEP"
                            placeholder="00000-000"
                            className="bsds-width-100"
                          />
                          <BsdsInput
                            value={editForm.street}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('street', target?.value || '');
                            }}
                            label="Rua"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                          />
                          <BsdsInput
                            value={editForm.number}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('number', target?.value || '');
                            }}
                            label="Número"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                          />
                          <BsdsInput
                            value={editForm.complement}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('complement', target?.value || '');
                            }}
                            label="Complemento"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                          />
                          <BsdsInput
                            value={editForm.district}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('district', target?.value || '');
                            }}
                            label="Bairro"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                          />
                          <BsdsInput
                            value={editForm.city}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('city', target?.value || '');
                            }}
                            label="Cidade"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                          />
                          <BsdsInput
                            value={editForm.state}
                            onInput={(e) => {
                              const target = e.target as HTMLInputElement;
                              handleEditFormChange('state', target?.value || '');
                            }}
                            label="Estado"
                            type="text"
                            color="primary"
                            platform="web"
                            className="bsds-width-100"
                          />
                        </div>
                      </BsdsCard>

                      {/* Action buttons */}
                      <div className="bsds-display-flex bsds-justify-content-end gap-4 bsds-mt-16">
                        <Button
                          onClick={handleCancelEdit}
                          color="secondary"
                          fill="outline"
                          disabled={isLoading}
                          loading={isLoading}
                          className="bsds-width-100"
                        >
                          Cancelar
                        </Button>
                        <Button
                          onClick={handleSave}
                          color="primary"
                          fill="solid"
                          loading={isLoading}
                          disabled={isLoading || !isFormValid()}
                          className="bsds-width-100"
                        >
                          Salvar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {/* Contract Info */}
                      <BsdsCard className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-p-16 bg-gray-100/30">
                        <div>
                          <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Número do Contrato</span>
                          <span className="bsds-text-regular font-semibold">
                            {participantDetail.contractNumber || '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Empresa</span>
                          <span className="bsds-text-regular">{participantDetail.companyName || '-'}</span>
                        </div>
                        <div>
                          <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Status</span>
                          <span className="bsds-text-regular">{mapStatus(participantDetail.status)}</span>
                        </div>
                      </BsdsCard>

                      {/* Personal Data */}
                      <div className="bsds-p-16" role="region" aria-label="Dados pessoais do participante">
                        <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                          <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
                          <h4 className="opacity-80">Dados Pessoais</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Nome Completo</span>
                            <span className="bsds-text-regular">{participantDetail.participantName || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">CPF</span>
                            <span className="bsds-text-regular">{formatCpf(participantDetail.cpf) || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Número de Matrícula</span>
                            <span className="bsds-text-regular">{participantDetail.registrationNumber || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Data de Nascimento</span>
                            <span className="bsds-text-regular">{formatDate(participantDetail.birthDate) || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Sexo Biológico</span>
                            <span className="bsds-text-regular">{mapBiologicalSex(participantDetail.biologicalSex) || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Estado Civil</span>
                            <span className="bsds-text-regular">{mapMaritalStatus(participantDetail.maritalStatus) || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Telefone Celular</span>
                            <span className="bsds-text-regular">{participantDetail.cellPhone || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">E-mail</span>
                            <span className="bsds-text-regular">{participantDetail.email || '-'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="bsds-p-16" role="region" aria-label="Endereço do participante">
                        <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                          <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
                          <h4 className="opacity-80">Endereço</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">CEP</span>
                            <span className="bsds-text-regular">{participantDetail.zipCode || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Rua</span>
                            <span className="bsds-text-regular">{participantDetail.street || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Número</span>
                            <span className="bsds-text-regular">{participantDetail.number || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Complemento</span>
                            <span className="bsds-text-regular">{participantDetail.complement || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Bairro</span>
                            <span className="bsds-text-regular">{participantDetail.district || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Cidade</span>
                            <span className="bsds-text-regular">{participantDetail.city || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Estado</span>
                            <span className="bsds-text-regular">{participantDetail.state || '-'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Enrollment Info */}
                      <div className="bsds-p-16" role="region" aria-label="Informações de matrícula">
                        <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                          <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
                          <h4 className="opacity-80">Informações de Matrícula</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Data de Início</span>
                            <span className="bsds-text-regular">{formatDate(participantDetail.startDate) || '-'}</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Data de Término</span>
                            <span className="bsds-text-regular">{formatDate(participantDetail.endDate) || '-'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action button */}
                      <div className="bsds-display-flex bsds-justify-content-end bsds-mt-16 gap-4">
                        <Button
                          onClick={handleBack}
                          color="secondary"
                          fill="outline"
                          disabled={isLoading}
                          loading={isLoading}
                          className="bsds-width-100"
                        >
                          Voltar
                        </Button>
                        <Button
                          onClick={handleEdit}
                          color="primary"
                          fill="solid"
                          disabled={isLoading}
                          loading={isLoading}
                          className="bsds-width-100"
                        >
                          Editar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </BsdsCard>
        </div>
      </Visibility>
    </div>
  );
};

export default SearchParticipantTab;
